import Papa from 'papaparse';
import * as Yup from 'yup';
import { FrameworkImportPayload, FrameworkImportControlInput } from 'src/types/contentImport';
import { FrameworkControlCsvHeaders } from 'src/api/types/misc';
import {
  convertDateToISO,
  convertToString,
  delimiters,
  ParseResult,
  ValidationErrorItem,
  validateRow,
} from 'src/utils/csv';

export interface FrameworkImportCsvRow {
  [FrameworkControlCsvHeaders.FrameworkID]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkIdentifier]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkName]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkShortName]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkAuthor]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkVersion]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkPublishDate]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkDescription]?: string | null;
  [FrameworkControlCsvHeaders.ID]?: string | null;
  [FrameworkControlCsvHeaders.FrameworkControlID]?: string | null;
  [FrameworkControlCsvHeaders.ControlName]?: string | null;
  [FrameworkControlCsvHeaders.ShortName]?: string | null;
  [FrameworkControlCsvHeaders.Strategy]?: string | null;
  [FrameworkControlCsvHeaders.Description]?: string | null;
}

const frameworkPathNames: Record<string, string> = {
  [FrameworkControlCsvHeaders.FrameworkID]: FrameworkControlCsvHeaders.FrameworkID,
  [FrameworkControlCsvHeaders.FrameworkIdentifier]: FrameworkControlCsvHeaders.FrameworkIdentifier,
  [FrameworkControlCsvHeaders.FrameworkName]: FrameworkControlCsvHeaders.FrameworkName,
  [FrameworkControlCsvHeaders.FrameworkShortName]: FrameworkControlCsvHeaders.FrameworkShortName,
  [FrameworkControlCsvHeaders.FrameworkAuthor]: FrameworkControlCsvHeaders.FrameworkAuthor,
  [FrameworkControlCsvHeaders.FrameworkVersion]: FrameworkControlCsvHeaders.FrameworkVersion,
  [FrameworkControlCsvHeaders.FrameworkPublishDate]: FrameworkControlCsvHeaders.FrameworkPublishDate,
  [FrameworkControlCsvHeaders.FrameworkDescription]: FrameworkControlCsvHeaders.FrameworkDescription,
  [FrameworkControlCsvHeaders.ID]: FrameworkControlCsvHeaders.ID,
  [FrameworkControlCsvHeaders.FrameworkControlID]: FrameworkControlCsvHeaders.FrameworkControlID,
  [FrameworkControlCsvHeaders.ControlName]: FrameworkControlCsvHeaders.ControlName,
  [FrameworkControlCsvHeaders.ShortName]: FrameworkControlCsvHeaders.ShortName,
  [FrameworkControlCsvHeaders.Strategy]: FrameworkControlCsvHeaders.Strategy,
  [FrameworkControlCsvHeaders.Description]: FrameworkControlCsvHeaders.Description,
};

const maxCharsMessage = (label: string, max: number) => `${label} can be up to ${max} characters.`;
const dateMessage = (label: string) => `${label} must be a valid date in DD-MM-YYYY format.`;

const controlRowHeaders = [
  FrameworkControlCsvHeaders.ID,
  FrameworkControlCsvHeaders.FrameworkControlID,
  FrameworkControlCsvHeaders.ControlName,
  FrameworkControlCsvHeaders.ShortName,
  FrameworkControlCsvHeaders.Strategy,
  FrameworkControlCsvHeaders.Description,
] as const;

const hasTextValue = (value: unknown): boolean => value !== null && value !== undefined && String(value).trim() !== '';

const hasRawControlRowContent = (row: FrameworkImportCsvRow): boolean => {
  return controlRowHeaders.some((header) => hasTextValue(row[header]));
};

const isValidDateInput = (value: unknown): boolean => {
  if (!hasTextValue(value)) return true;
  return convertDateToISO(value) !== null;
};

const buildFrameworkValidationSchema = (requireFrameworkName: boolean) =>
  Yup.object({
    [FrameworkControlCsvHeaders.FrameworkID]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkID, 255)),
    [FrameworkControlCsvHeaders.FrameworkIdentifier]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkIdentifier, 255)),
    [FrameworkControlCsvHeaders.FrameworkName]: requireFrameworkName
      ? Yup.string()
          .required(`${FrameworkControlCsvHeaders.FrameworkName} is required.`)
          .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkName, 255))
      : Yup.string().nullable().max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkName, 255)),
    [FrameworkControlCsvHeaders.FrameworkShortName]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkShortName, 255)),
    [FrameworkControlCsvHeaders.FrameworkAuthor]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkAuthor, 255)),
    [FrameworkControlCsvHeaders.FrameworkVersion]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkVersion, 255)),
    [FrameworkControlCsvHeaders.FrameworkPublishDate]: Yup.string()
      .nullable()
      .test(
        'valid-framework-publish-date',
        dateMessage(FrameworkControlCsvHeaders.FrameworkPublishDate),
        (value) => isValidDateInput(value),
      ),
    [FrameworkControlCsvHeaders.FrameworkDescription]: Yup.string()
      .nullable()
      .max(3000, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkDescription, 3000)),
    [FrameworkControlCsvHeaders.ID]: Yup.string().nullable().max(255, maxCharsMessage(FrameworkControlCsvHeaders.ID, 255)),
    [FrameworkControlCsvHeaders.FrameworkControlID]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.FrameworkControlID, 255)),
    [FrameworkControlCsvHeaders.ControlName]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.ControlName, 255))
      .test(
        'control-name-required',
        `${FrameworkControlCsvHeaders.ControlName} is required for each control row.`,
        function (value) {
          const row = this.parent as FrameworkImportCsvRow;
          if (!hasRawControlRowContent(row)) return true;
          return hasTextValue(value);
        },
      ),
    [FrameworkControlCsvHeaders.ShortName]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.ShortName, 255)),
    [FrameworkControlCsvHeaders.Strategy]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(FrameworkControlCsvHeaders.Strategy, 255)),
    [FrameworkControlCsvHeaders.Description]: Yup.string()
      .nullable()
      .max(2048, maxCharsMessage(FrameworkControlCsvHeaders.Description, 2048)),
  });

const frameworkHeaders = [
  FrameworkControlCsvHeaders.FrameworkID,
  FrameworkControlCsvHeaders.FrameworkIdentifier,
  FrameworkControlCsvHeaders.FrameworkName,
  FrameworkControlCsvHeaders.FrameworkShortName,
  FrameworkControlCsvHeaders.FrameworkAuthor,
  FrameworkControlCsvHeaders.FrameworkVersion,
  FrameworkControlCsvHeaders.FrameworkPublishDate,
  FrameworkControlCsvHeaders.FrameworkDescription,
  FrameworkControlCsvHeaders.ID,
  FrameworkControlCsvHeaders.FrameworkControlID,
  FrameworkControlCsvHeaders.ControlName,
  FrameworkControlCsvHeaders.ShortName,
  FrameworkControlCsvHeaders.Strategy,
  FrameworkControlCsvHeaders.Description,
] as const;

const createMetadataMismatchErrors = (rows: FrameworkImportCsvRow[]): ValidationErrorItem[] => {
  if (!rows.length) return [];

  const first = rows[0];
  const metadataHeaders = [
    FrameworkControlCsvHeaders.FrameworkID,
    FrameworkControlCsvHeaders.FrameworkIdentifier,
    FrameworkControlCsvHeaders.FrameworkName,
    FrameworkControlCsvHeaders.FrameworkShortName,
    FrameworkControlCsvHeaders.FrameworkAuthor,
    FrameworkControlCsvHeaders.FrameworkVersion,
    FrameworkControlCsvHeaders.FrameworkPublishDate,
    FrameworkControlCsvHeaders.FrameworkDescription,
  ] as const;

  return rows
    .map((row, index) => {
      const errors = metadataHeaders
        .filter((header) => String(row[header] ?? '').trim() !== String(first[header] ?? '').trim())
        .map((header) => ({
          path: header,
          message: `${header} must match across all rows in the file.`,
        }));

      return errors.length ? { rowIndex: index + 1, errors } : null;
    })
    .filter(Boolean) as ValidationErrorItem[];
};

const hasFrameworkRowContent = (row: FrameworkImportControlInput) => {
  return Boolean(row.controlIdentifier || row.name || row.shortname || row.strategy || row.description);
};

export const generateFrameworkTemplateLink = () => {
  const csvString = frameworkHeaders.join(';') + '\n';
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  return URL.createObjectURL(blob);
};

export const parseFrameworkCsv = async ({
  file,
  existingControlIds,
  requireFrameworkName = true,
}: {
  file: File;
  existingControlIds: string[];
  requireFrameworkName?: boolean;
}): Promise<{
  payload: FrameworkImportPayload | null;
  validationErrors: ValidationErrorItem[];
  createdCount: number;
  updatedCount: number;
}> => {
  const tryParse = (delimiter: string) =>
    new Promise<ParseResult<FrameworkImportCsvRow>>((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        delimiter,
        complete: resolve,
        error: reject,
      });
    });

  let result: ParseResult<FrameworkImportCsvRow> | null = null;
  for (const delimiter of delimiters) {
    const parsed = await tryParse(delimiter);
    if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
      result = parsed;
      break;
    }
  }

  if (!result || !result.data?.length) {
    return {
      payload: null,
      validationErrors: [{ rowIndex: 0, errors: [{ path: 'Data', message: 'No framework data found.' }] }],
      createdCount: 0,
      updatedCount: 0,
    };
  }

  if (result.data.length > 60) {
    return {
      payload: null,
      validationErrors: [
        {
          rowIndex: 0,
          errors: [{ path: 'Data', message: 'This file has more than 60 controls. Please upload up to 60 rows.' }],
        },
      ],
      createdCount: 0,
      updatedCount: 0,
    };
  }

  const validationSchema = buildFrameworkValidationSchema(requireFrameworkName);

  const rawValidationResults = await Promise.all(
    result.data.map((row, index) =>
      validateRow({
        row,
        rowIndex: index + 1,
        schema: validationSchema,
        pathNames: frameworkPathNames,
      }),
    ),
  );

  const validationErrors = rawValidationResults.filter((item) => item?.errors?.length) as ValidationErrorItem[];
  const metadataConsistencyErrors = createMetadataMismatchErrors(result.data);
  const allErrors = [...validationErrors, ...metadataConsistencyErrors];

  if (allErrors.length) {
    return {
      payload: null,
      validationErrors: allErrors,
      createdCount: 0,
      updatedCount: 0,
    };
  }

  const first = result.data[0];
  const controls = result.data
    .map<FrameworkImportControlInput>((row) => ({
      id: convertToString(row[FrameworkControlCsvHeaders.ID]),
      controlIdentifier: convertToString(row[FrameworkControlCsvHeaders.FrameworkControlID]),
      name: convertToString(row[FrameworkControlCsvHeaders.ControlName]),
      shortname: convertToString(row[FrameworkControlCsvHeaders.ShortName]),
      strategy: convertToString(row[FrameworkControlCsvHeaders.Strategy]),
      description: convertToString(row[FrameworkControlCsvHeaders.Description]),
    }))
    .filter((row) => hasFrameworkRowContent(row));

  const payload: FrameworkImportPayload = {
    framework: {
      id: convertToString(first[FrameworkControlCsvHeaders.FrameworkID]),
      frameworkIdentifier: convertToString(first[FrameworkControlCsvHeaders.FrameworkIdentifier]),
      name: convertToString(first[FrameworkControlCsvHeaders.FrameworkName]) || '',
      shortname: convertToString(first[FrameworkControlCsvHeaders.FrameworkShortName]),
      author: convertToString(first[FrameworkControlCsvHeaders.FrameworkAuthor]),
      version: convertToString(first[FrameworkControlCsvHeaders.FrameworkVersion]),
      publishDate: convertDateToISO(first[FrameworkControlCsvHeaders.FrameworkPublishDate]),
      description: convertToString(first[FrameworkControlCsvHeaders.FrameworkDescription]),
    },
    controls,
  };

  const updatedCount = controls.filter((control) => control.id && existingControlIds.includes(control.id)).length;
  const createdCount = controls.filter((control) => !control.id || !existingControlIds.includes(control.id)).length;

  return {
    payload,
    validationErrors: [],
    createdCount,
    updatedCount,
  };
};
